import React, { JSX, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useEventDetail } from 'hooks/useEventDetail'
import { useEventAccess } from 'hooks/useEventAccess'
import { useEventYouTubeSessions } from 'hooks/useYouTubeSessions'
import { eventHasYouTubeSource } from 'components/EventYouTubeSources'
import { YouTubeSessionWidget } from 'components/YouTubeSessionWidget'
import { eventSectionPath } from 'lib/paths'

export const YOUTUBE_CHAT_MISSING_TITLE = 'YouTube votes are not being counted'
export const YOUTUBE_CHAT_MISSING_BODY = 'No YouTube chat is running for this stream. Start YouTube chat so YouTube votes count.'
export const YOUTUBE_CHAT_ASK_OWNER = 'Ask the stream owner to start YouTube chat.'
export const YOUTUBE_CHAT_NO_SOURCE = 'This stream has no YouTube source. Add one on the Integrations page.'

export const YouTubeChatMissingWarning = ({
  eventId,
  visible,
  onDismiss
}: {
  eventId: string
  visible: boolean
  onDismiss: () => void
}): JSX.Element | null => {
  const { event } = useEventDetail()
  const { canManage } = useEventAccess()
  const store = useEventYouTubeSessions(eventId, false)
  const { refresh } = store
  const [checked, setChecked] = useState(false)
  const readRef = useRef<Promise<void> | null>(null)

  useEffect(() => {
    let cancelled = false
    if (readRef.current == null) readRef.current = refresh()
    void readRef.current.then(() => { if (!cancelled) setChecked(true) })
    return () => { cancelled = true }
  }, [refresh])

  const running = store.sessions.some((s) => s.status === 'RUNNING')
  if (!visible || !checked || running) return null

  const hasSource = eventHasYouTubeSource(event)
  let action: JSX.Element
  if (!hasSource) {
    action = (
      <p className='mt-1' data-testid='yt-chat-missing-no-source'>
        {YOUTUBE_CHAT_NO_SOURCE}{' '}
        <Link to={eventSectionPath(eventId, 'integrations')} className='font-semibold text-amber-100 underline hover:text-white'>
          Open Integrations
        </Link>
      </p>
    )
  } else if (canManage) {
    action = (
      <div className='mt-2 flex' data-testid='yt-chat-missing-start'>
        <YouTubeSessionWidget
          scope={{ kind: 'event', eventId }}
          variant='compact'
          ownSourceAttached={event.youtubeSourceAttached === true}
        />
      </div>
    )
  } else {
    action = <p className='mt-1' data-testid='yt-chat-missing-ask-owner'>{YOUTUBE_CHAT_ASK_OWNER}</p>
  }

  return (
    <div
      role='status'
      data-testid='yt-chat-missing-warning'
      className='mt-2 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200'
    >

      <span className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400' aria-hidden='true' />
      <div className='min-w-0 flex-1'>
        <p className='font-semibold text-amber-100'>{YOUTUBE_CHAT_MISSING_TITLE}</p>
        <p className='mt-0.5'>{YOUTUBE_CHAT_MISSING_BODY}</p>
        {action}
      </div>
      <button
        type='button'
        onClick={onDismiss}
        aria-label='Dismiss'
        title='Dismiss'
        data-testid='yt-chat-missing-dismiss'
        className='-m-1 shrink-0 rounded p-1 text-amber-200/70 transition-colors hover:bg-amber-500/10 hover:text-amber-100'
      >
        <XMarkIcon className='size-3.5' aria-hidden='true' />
      </button>
    </div>
  )
}
